import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, InputAdornment, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import {
  type SelfEmploymentIncomeFormType,
  getSelfEmploymentIncomeFormSchema,
} from "Forms/Schemas/Budget/Incomes/selfEmploymentIncomeFormSchema";
import {
  getGetBudgetOverviewQueryKey,
  getListIncomesQueryKey,
  useCreateIncome,
  useDeleteIncome,
  useUpdateIncome,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type Income, IncomeAmountAmountType, IncomeBodyIncomeType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { forwardRef, useContext, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

type Props = {
  onClose: () => void;
  clientId?: string;
  edit?: Income;
};

export const SelfEmploymentIncomeForm = forwardRef<{}, Props>(({ onClose, clientId, edit }, ref) => {
  const { t } = useResource();
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

  const queryClient = useQueryClient();
  const { mutate: putIncome } = useUpdateIncome();
  const { mutate } = useCreateIncome();
  const { mutate: deleteIncome } = useDeleteIncome();

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<SelfEmploymentIncomeFormType>({
    resolver: yupResolver(getSelfEmploymentIncomeFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      amountValue: (edit?.incomeAmounts[0]?.amount.value.toString() as any) || "",
    },
  });

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)();
    },
    deleteIncome: () => {
      handleDelete();
    },
  }));

  const onSubmit = async (data: SelfEmploymentIncomeFormType) => {
    if (edit) {
      putIncome(
        {
          analysisId,
          versionId,
          incomeId: edit.guid,
          data: {
            incomeType: IncomeBodyIncomeType.self_employment,
            clientGuid: clientId ?? "",
            incomeAmounts: [
              {
                amountType: IncomeAmountAmountType.self_employment,
                amount: {
                  value: data.amountValue ?? 0,
                  currencyCode: "CZK",
                  frequency: AmountFrequency.monthly,
                },
                name: "",
              },
            ],
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
            onClose();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
          },
        },
      );
    } else {
      mutate(
        {
          data: {
            incomeType: IncomeBodyIncomeType.self_employment,
            clientGuid: clientId ?? "",
            incomeAmounts: [
              {
                amountType: IncomeAmountAmountType.self_employment,
                amount: {
                  value: data.amountValue ?? 0,
                  currencyCode: "CZK",
                  frequency: AmountFrequency.monthly,
                },
                name: "",
              },
            ],
          },
          analysisId,
          versionId,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
            onClose();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
          },
        },
      );
    }
  };

  const handleDelete = () => {
    deleteIncome(
      {
        analysisId,
        versionId,
        incomeId: edit?.guid ?? "",
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
          onClose();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
        },
      },
    );
  };

  return (
    <Stack spacing={Spacing.XXL} pb={Spacing.XXL} sx={{ height: "100%" }}>
      <Box>
        <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
          {t(Resources.AnalysisDetail.Budget.Incomes.IncomeTypes.SelfEmployment)}
        </Typography>

        <Grid container spacing={Spacing.XL}>
          <Grid size={12}>
            <BlFormInput
              control={control}
              disabled={readOnly}
              name="amountValue"
              label={t(Resources.AnalysisDetail.Budget.Incomes.Form.Fields.RealAverageIncome)}
              errors={errors}
              labelEndAdornment={<StyledLabelAdornment />}
              inputEndAdornment={
                <InputAdornment position="end">{`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}</InputAdornment>
              }
              mask={[
                {
                  mask: Number,
                  thousandsSeparator: " ",
                  scale: 2,
                  min: 0,
                },
              ]}
              placeholder="0"
            />
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
});
